import React, { useEffect, useState } from "react";
import { Button, Col, Form, Input, Radio, RadioChangeEvent, Row, Select, Space, Upload } from "antd";

import { useAsyncAxios, utilAxiosFormWithAuth, utilAxiosWithAuth } from "../utils/customAxios";
import { AlertType, useAlert } from "../provider/AlertProvider";
import type { UploadFile } from "antd/es/upload/interface";
import { UploadOutlined } from "@ant-design/icons";
import type { UploadProps } from "antd";

import * as Types from "../commons/typeDTO";
import * as Request from "../commons/request";

const formItemLayout = {
    labelCol: {
        sm: {
            span: 5,
        },
    },
    wrapperCol: {
        sm: {
            span: 19,
        },
    },
};

function RegisterRentManual({
    editMode,
    rentManual,
    onRegisterRentManual,
}: {
    editMode: string;
    rentManual?: Types.RentManualDto;
    onRegisterRentManual?: () => void;
}) {
    const [form] = Form.useForm();
    const alert = useAlert();

    const [rentManualName, setRentManualName] = useState("");
    const [rentManualVersion, setRentManualVersion] = useState("");
    const [attachFiles, setAttachFiles] = useState<Array<any>>([]);

    const requestAxiosAddRentManual = async (data: any) => {
        // console.log("data : " + data);

        const response = await utilAxiosFormWithAuth().post(Request.RENT_MANUAL_URL, data);

        return response.data;
    };

    const {
        loading: loadingAddRentManual,
        error: errorAddRentManual,
        data: resultAddRentManual,
        execute: requestAddRentManual,
    } = useAsyncAxios(requestAxiosAddRentManual);

    useEffect(() => {
        if (rentManual === undefined) return;

        form.setFieldsValue(rentManual);
    }, [rentManual]);

    useEffect(() => {
        if (resultAddRentManual === null) return;

        if (onRegisterRentManual) onRegisterRentManual();

        alert.setAlert(AlertType.SUCCESS, "매뉴얼 추가 성공", "매뉴얼 추가를 완료하였습니다.");
    }, [resultAddRentManual]);

    useEffect(() => {
        if (errorAddRentManual === null) return;

        console.log("errorRentManual", errorAddRentManual);
        alert.setAlert(errorAddRentManual.response.data.code, "매뉴얼 추가 실패", errorAddRentManual.response.data.message);
    }, [errorAddRentManual]);

    const requestAxiosUpdateRentManual = async (data: any, rentManualId: number) => {
        const response = await utilAxiosFormWithAuth().put(Request.RENT_MANUAL_URL + "/" + rentManualId, data);

        return response.data;
    };

    const {
        loading: loadingUpdateRentManual,
        error: errorUpdateRentManual,
        data: resultUpdateRentManual,
        execute: requestUpdateRentManual,
    } = useAsyncAxios(requestAxiosUpdateRentManual);

    useEffect(() => {
        if (resultUpdateRentManual === null) return;

        if (onRegisterRentManual) onRegisterRentManual();

        alert.setAlert(AlertType.SUCCESS, "매뉴얼 수정 성공", "매뉴얼 수정을 완료하였습니다.");
    }, [resultUpdateRentManual]);

    useEffect(() => {
        if (errorUpdateRentManual === null) return;

        console.log("errorUpdateRentManual", errorUpdateRentManual);
        alert.setAlert(errorUpdateRentManual.response.data.code, "매뉴얼 수정 실패", errorUpdateRentManual.response.data.message);
    }, [errorUpdateRentManual]);

    const onFinish = async (values: any) => {
        const formData = new FormData();
        formData.append("rentManualDto", new Blob([JSON.stringify(values)], { type: "application/json" }));

        for (const file in attachFiles) {
            const response = await fetch(URL.createObjectURL(attachFiles[file]));
            const blob = await response.blob();
            const data = new File([blob], attachFiles[file].name, { type: blob.type });

            formData.append("rentManualFile", data);
        }
        if (editMode === "add") requestAddRentManual(formData);
        else if (editMode === "update") requestUpdateRentManual(formData, values.rentManualId);
    };

    const propsAttachFiles = {
        onRemove: (file: any) => {
            const index = attachFiles.indexOf(file);
            const newAttachFiles = attachFiles.slice();
            newAttachFiles.splice(index, 1);
            setAttachFiles(newAttachFiles);
        },
        beforeUpload: (file: any) => {
            setAttachFiles([...attachFiles, file]);
            return false;
        },
        fileList: attachFiles,
    };

    return (
        <div>
            <Form form={form} onFinish={onFinish}>
                <Space size={20} direction="vertical" style={{ width: "100%" }}>
                    <div>
                        <Row>
                            <Col span={12}>
                                <Form.Item className="searchForm" {...formItemLayout} name="rentManualId" label="매뉴얼 ID">
                                    <Input readOnly />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    className="searchForm"
                                    {...formItemLayout}
                                    name="rentManualName"
                                    label="매뉴얼 이름"
                                    rules={[
                                        {
                                            required: true,
                                            message: "매뉴얼 이름을 입력해 주세요.",
                                        },
                                    ]}
                                >
                                    <Input placeholder="매뉴얼 이름을 입력해 주세요." onChange={(e) => setRentManualName(e.target.value)} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row>
                            <Col span={12}>
                                <Form.Item
                                    className="searchForm"
                                    {...formItemLayout}
                                    name="rentManualVersion"
                                    label="매뉴얼 버젼"
                                    rules={[
                                        {
                                            required: true,
                                            message: "매뉴얼 버젼을 입력해 주세요.",
                                        },
                                    ]}
                                >
                                    <Input placeholder="매뉴얼 버젼을 입력해 주세요." onChange={(e) => setRentManualVersion(e.target.value)} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    className="searchForm"
                                    {...formItemLayout}
                                    name="rentManualFile"
                                    label="매뉴얼 파일"
                                    rules={[
                                        {
                                            required: editMode === "add" ? true : false,
                                            message: "매뉴얼 파일을 추가해 주세요.",
                                        },
                                    ]}
                                >
                                    <Upload {...propsAttachFiles} multiple={true}>
                                        <Button icon={<UploadOutlined />}>Upload PDF only</Button>
                                    </Upload>
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>

                    <Row justify="end">
                        <Form.Item>
                            <Button type="primary" htmlType="submit">
                                {editMode === "add" ? "등록" : "수정"}
                            </Button>
                        </Form.Item>
                    </Row>
                </Space>
            </Form>
        </div>
    );
}

export default RegisterRentManual;
